import React, { useEffect, useContext } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  useNavigate,
  useLocation,
  Location,
  Navigate,
  Outlet,
} from "react-router-dom";
import { QueryParamProvider } from "use-query-params";
import { CustomProvider } from "rsuite";
import RequireAuth from "./ui/components/auth/require-auth";
import Auth from "./ui/components/auth/login";
import Layout from "./ui/components/auth/layout";
import AdminPage from "./ui/admin/admin-page";
import "rsuite/dist/rsuite.min.css";
import { NavbarTab } from "./ui/components/navbar/Navbar";
import ConstructorFlashApps from "./ui/components/constructor/constructor";
import { PanelUserAccessScope } from "@bprotsyk/aso-core";
import Offers from "./ui/containers/offers/offers";
import Domains from "./ui/containers/domains/domains";
import Apps from "ui/containers/app/list/apps";
import Lists from "./ui/containers/lists/list";
import Settings from "./ui/containers/settings/settings";
import './App.scss';
import Caps from "./ui/containers/caps/caps";
import ClickData from "./ui/containers/click-data/click-data";

import AppInfo from "ui/containers/app-info/app-info";
import { createUniqueSite } from "./web-constructor/svgGenerator";
import AppHub from "ui/containers/container/app-hub";
import { createLogger } from './utils/logger';
import { getDefaultPanelPath, readStoredRoles } from "./utils/panel-access";

const logger = createLogger('app');

declare global {
  interface Window {
    sleep: (ms: number) => Promise<void>;
  }
}



Array.from(document.querySelectorAll(".accordion-el")).filter(el => el.classList.contains("is-active")).forEach(el => {
  logger.debug('active accordion element', el);
});




function App() {
  return (
    <CustomProvider theme="dark">
      <div className="app-container">
        <BrowserRouter>
          <QueryParamProvider ReactRouterRoute={RouteAdapter as any}>
            <Routes>
              <Route path="/" element={<Navigate to={localStorage.getItem("token") ? getDefaultPanelPath(readStoredRoles()) : "/auth"} />} />

              <Route
                path="/auth"
                element={<AppHub component={<Auth />} tab={NavbarTab.AUTH} />}
              />

              <Route
                element={
                  <RequireAuth allowedRoles={[PanelUserAccessScope.ADMIN]} />
                }
              >
                <Route
                  path="/offers"
                  element={
                    <AppHub component={<Offers />} tab={NavbarTab.OFFERS} />
                  }
                />
              </Route>
              <Route
                element={
                  <RequireAuth allowedRoles={[PanelUserAccessScope.ADMIN, PanelUserAccessScope.TESTER]} />
                }
              >
                <Route
                  path="/apps"
                  element={
                    <AppHub component={<Apps />} tab={NavbarTab.APPS} />
                  }
                />
              </Route>
              <Route
                element={
                  <RequireAuth allowedRoles={[PanelUserAccessScope.ADMIN, PanelUserAccessScope.DEPLOYER]} />
                }
              >
                <Route
                  path="/domains"
                  element={
                    <AppHub component={<Domains />} tab={NavbarTab.DOMAINS} />
                  }
                />
              </Route>
              
              <Route
                element={
                  <RequireAuth allowedRoles={[PanelUserAccessScope.ADMIN]} />
                }
              >
                <Route
                  path="/click-data"
                  element={
                    <AppHub component={<ClickData />} tab={NavbarTab.CLICK_DATA} />
                  }
                />
              </Route>
              <Route
                element={
                  <RequireAuth allowedRoles={[PanelUserAccessScope.ADMIN]} />
                }
              >
                <Route
                  path="/offer-lists"
                  element={
                    <AppHub component={<Lists />} tab={NavbarTab.OFFER_LISTS} />
                  }
                />
              </Route>
              <Route
                element={
                  <RequireAuth allowedRoles={[PanelUserAccessScope.ADMIN]} />
                }
              >
                <Route
                  path="/settings"
                  element={
                    <AppHub component={<Settings />} tab={NavbarTab.SETTINGS} />
                  }
                />
              </Route>
              <Route
                element={
                  <RequireAuth allowedRoles={[PanelUserAccessScope.ADMIN, PanelUserAccessScope.CAPS_RW]} />
                }
              >
                <Route
                  path="/caps"
                  element={
                    <AppHub component={<Caps />} tab={NavbarTab.CAPS} />
                  }
                />
              </Route>
              <Route
              element={
                <RequireAuth allowedRoles={[PanelUserAccessScope.ADMIN, PanelUserAccessScope.MANAGER]} />
              }
            >
              <Route
                path="/app-info"
                element={
                  <AppHub
                    component={<AppInfo />}
                    tab={NavbarTab.APPS_INFO}
                  />
                }
              />
            </Route>
          </Routes>
          </QueryParamProvider>
        </BrowserRouter>
      </div>
    </CustomProvider>
  );
}

const RouteAdapter = ({ children }: { children: any }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const adaptedHistory = React.useMemo(
    () => ({
      replace(location: Location) {
        navigate(location, { replace: true, state: location.state });
      },
      push(location: Location) {
        navigate(location, { replace: false, state: location.state });
      },
    }),
    [navigate]
  );
  return children({ history: adaptedHistory, location });
};

export default App;
